import React from "react";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../common/SafeIcon";

const { FiChevronLeft, FiChevronRight } = FiIcons;

// Builds a windowed page list so a large totalPages never renders one button
// per page (that overflowed the row on narrow/portrait viewports). Always
// keeps the first and last page, plus a small window around currentPage, and
// collapses the rest into "…" placeholders.
const getPageItems = (currentPage, totalPages) => {
  const siblingCount = 1;
  const totalVisible = siblingCount * 2 + 5; // first + last + current + 2 ellipses + siblings

  if (totalPages <= totalVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSibling = Math.max(currentPage - siblingCount, 1);
  const rightSibling = Math.min(currentPage + siblingCount, totalPages);

  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < totalPages - 1;

  const items = [1];
  if (showLeftEllipsis) items.push("left-ellipsis");
  for (let page = leftSibling === 1 ? 2 : leftSibling; page <= (rightSibling === totalPages ? totalPages - 1 : rightSibling); page++) {
    items.push(page);
  }
  if (showRightEllipsis) items.push("right-ellipsis");
  items.push(totalPages);

  return items;
};

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  const pageItems = getPageItems(currentPage, totalPages);

  return (
    <div className="flex justify-center overflow-x-auto">
      <nav className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={(e) => { onPageChange(Math.max(currentPage - 1, 1)); e.currentTarget.blur(); }}
          disabled={currentPage === 1}
          className="p-2 rounded-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed text-gray-400 hover:text-white outline-none focus-visible:ring-2 focus-visible:ring-violet-500 shrink-0"
          style={{ border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.04)" }}
        >
          <SafeIcon icon={FiChevronLeft} className="w-4 h-4" />
        </button>

        {pageItems.map((page) =>
          typeof page === "number" ? (
            <button
              key={page}
              type="button"
              onClick={(e) => { onPageChange(page); e.currentTarget.blur(); }}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-violet-500 shrink-0"
              style={
                currentPage === page
                  ? { background: "linear-gradient(135deg,#7c3aed,#6d28d9)", color: "#fff", border: "none" }
                  : { border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.04)", color: "#9ca3af" }
              }
            >
              {page}
            </button>
          ) : (
            <span key={page} className="w-8 h-8 flex items-center justify-center text-sm text-gray-500 shrink-0">
              …
            </span>
          )
        )}

        <button
          type="button"
          onClick={(e) => { onPageChange(Math.min(currentPage + 1, totalPages)); e.currentTarget.blur(); }}
          disabled={currentPage === totalPages}
          className="p-2 rounded-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed text-gray-400 hover:text-white outline-none focus-visible:ring-2 focus-visible:ring-violet-500 shrink-0"
          style={{ border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.04)" }}
        >
          <SafeIcon icon={FiChevronRight} className="w-4 h-4" />
        </button>
      </nav>
    </div>
  );
};

export default Pagination;
